/**
 * ScanDetailModal Component
 * Modal for viewing scan details with signals table
 */

import React from 'react';
import { Tag, Tooltip, Button, Alert, Space, Badge } from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import { DetailModal, TGDataTable } from '../../../components/common';
import { colors } from '../../../theme/designTokens';

/**
 * Trust Score Display Component
 */
function TrustScoreDisplay({ score }) {
  if (score === null || score === undefined) return <span style={{ color: colors.text.secondary }}>N/A</span>;

  let color = colors.severity.low;
  if (score >= 80) color = colors.status.pass;
  else if (score >= 60) color = colors.status.warning;
  else color = colors.status.fail;

  return (
    <span style={{ fontWeight: 600, fontSize: 24, color }}>
      {score.toFixed(1)}
    </span>
  );
}

/**
 * Status Tag Component
 */
function StatusTag({ status }) {
  const statusConfig = {
    pass: { color: colors.status.pass, icon: <CheckCircleOutlined />, label: 'Pass' },
    fail: { color: colors.status.fail, icon: <CloseCircleOutlined />, label: 'Fail' },
    warning: { color: colors.status.warning, icon: <ExclamationCircleOutlined />, label: 'Warning' },
  };

  const config = statusConfig[status] || { color: 'default', label: status };

  return (
    <Tag color={config.color} icon={config.icon}>
      {config.label}
    </Tag>
  );
}

/**
 * Scan Status Tag Component
 */
function ScanStatusTag({ status }) {
  const statusConfig = {
    completed: { color: colors.status.pass, icon: <CheckCircleOutlined />, label: 'Completed' },
    failed: { color: colors.status.fail, icon: <CloseCircleOutlined />, label: 'Failed' },
    running: { color: colors.status.running, icon: <ExclamationCircleOutlined />, label: 'Running' },
    pending: { color: colors.status.info, label: 'Pending' },
  };

  const config = statusConfig[status] || statusConfig.pending;

  return (
    <Tag color={config.color} icon={config.icon}>
      {config.label}
    </Tag>
  );
}

/**
 * ScanDetailModal Component
 */
function ScanDetailModal({
  visible,
  scan,
  signals,
  loading,
  statusFilter,
  onClose,
  onViewEvidence,
  onClearFilter,
  fetchAllSignals,
}) {
  // Generate dynamic filters from signals data
  const controlCodeFilters = React.useMemo(() => {
    if (!signals || signals.length === 0) return [];
    const codes = [...new Set(signals.map(s => s.control_code))].sort();
    return codes.map(code => ({ label: code, value: code }));
  }, [signals]);

  const controlTitleFilters = React.useMemo(() => {
    if (!signals || signals.length === 0) return [];
    const titles = [...new Set(signals.map(s => s.control_title).filter(t => t))].sort();
    return titles.map(title => ({ label: title, value: title }));
  }, [signals]);

  const confidenceFilters = React.useMemo(() => {
    if (!signals || signals.length === 0) return [];
    const values = [...new Set(signals.map(s => s.confidence).filter(c => c != null))].sort((a, b) => a - b);
    return values.map(val => ({ label: `${val}%`, value: val }));
  }, [signals]);

  const evidenceCountFilters = React.useMemo(() => {
    if (!signals || signals.length === 0) return [];
    const counts = [...new Set(signals.map(s => s.evidence_count || 0))].sort((a, b) => a - b);
    return counts.map(count => ({ label: count.toString(), value: count }));
  }, [signals]);

  // Metadata for DetailModal
  const metadata = scan ? [
    { label: 'Scan ID', value: `#${scan.id}`, span: 1 },
    { label: 'Trust Score', value: <TrustScoreDisplay score={scan.trust_score} />, span: 1 },
    { label: 'Status', value: <ScanStatusTag status={scan.status} />, span: 1 },
    { label: 'Started At', value: new Date(scan.started_at).toLocaleString(), span: 1 },
    {
      label: 'Controls Scanned',
      value: (
        <Badge
          count={scan.controls_scanned || 0}
          showZero
          style={{ backgroundColor: colors.primary[500] }}
        />
      ),
      span: 1,
    },
    {
      label: 'Pass',
      value: (
        <span style={{ fontWeight: 600, fontSize: 18, color: colors.status.pass }}>
          {scan.pass_count || 0}
        </span>
      ),
      span: 1,
    },
    {
      label: 'Fail',
      value: (
        <span style={{ fontWeight: 600, fontSize: 18, color: colors.status.fail }}>
          {scan.fail_count || 0}
        </span>
      ),
      span: 1,
    },
    {
      label: 'Warnings',
      value: (
        <span style={{ fontWeight: 600, fontSize: 18, color: colors.status.warning }}>
          {scan.warning_count || 0}
        </span>
      ),
      span: 1,
    },
  ] : [];

  // Signal columns for TGDataTable
  const signalColumns = [
    {
      key: 'control_code',
      title: 'Control',
      dataIndex: 'control_code',
      width: 180,
      fixed: 'left',
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: controlCodeFilters,
    },
    {
      key: 'control_title',
      title: 'Control Name',
      dataIndex: 'control_title',
      width: 220,
      render: (title) => title || '—',
      sortable: true,
      filterable: true,
      filterType: 'text',
    },
    {
      key: 'control_domain',
      title: 'Domain',
      dataIndex: 'control_domain',
      width: 160,
      render: (domain) =>
        domain ? (
          <Tag color={colors.primary[500]}>{domain}</Tag>
        ) : (
          <Tag color="default">No Domain</Tag>
        ),
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: [
        { label: 'Data Governance', value: 'Data Governance' },
        { label: 'Model Governance', value: 'Model Governance' },
      ],
    },
    {
      key: 'status',
      title: 'Status',
      dataIndex: 'status',
      width: 110,
      render: (status) => <StatusTag status={status} />,
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: [
        { label: 'Pass', value: 'pass' },
        { label: 'Fail', value: 'fail' },
        { label: 'Warning', value: 'warning' },
      ],
    },
    {
      key: 'confidence',
      title: 'Confidence',
      dataIndex: 'confidence',
      width: 110,
      align: 'center',
      render: (confidence) => `${confidence}%`,
      sortable: true,
      filterable: true,
      filterType: 'number',
    },
    {
      key: 'evidence_count',
      title: 'Evidence',
      dataIndex: 'evidence_count',
      width: 100,
      align: 'center',
      render: (count, record) => {
        const actualCount = count || 0;
        if (actualCount === 0) return <span style={{ color: colors.text.secondary }}>0</span>;
        return (
          <Tooltip title={`View ${actualCount} evidence event(s)`}>
            <Button
              type="link"
              size="small"
              onClick={() => onViewEvidence(record)}
              style={{ padding: 0 }}
            >
              <Badge
                count={actualCount}
                showZero={false}
                style={{ backgroundColor: colors.primary[500] }}
              />
            </Button>
          </Tooltip>
        );
      },
      sortable: true,
      filterable: true,
      filterType: 'number',
    },
    {
      key: 'rationale',
      title: 'Rationale',
      dataIndex: 'rationale',
      width: 300,
      render: (rationale) => {
        if (!rationale) return <span style={{ color: colors.text.secondary }}>—</span>;
        const truncated = rationale.length > 80 ? rationale.substring(0, 80) + '...' : rationale;
        return (
          <Tooltip title={rationale} placement="topLeft">
            <div
              style={{
                maxWidth: '300px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                cursor: 'help',
              }}
            >
              {truncated}
            </div>
          </Tooltip>
        );
      },
      sortable: true,
      filterable: true,
      filterType: 'text',
    },
  ];

  // Early return after all hooks are called
  if (!scan) return null;

  return (
    <DetailModal
      visible={visible}
      onClose={onClose}
      title={`Scan #${scan.id} Details`}
      width={1300}
      metadata={metadata}
      destroyOnClose
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {statusFilter && (
          <Alert
            message={`Showing controls with status: ${statusFilter.toUpperCase()}`}
            type="info"
            closable
            onClose={onClearFilter}
          />
        )}

        <TGDataTable
          data={signals}
          columns={signalColumns}
          loading={loading}
          rowKey="id"
          pagination="client"
          pageSize={10}
          showExport
          showColumnConfig
          showFilters
          exportFilename={`scan_${scan.id}_signals`}
          presetKey={`trustgrid.scan_${scan.id}_signals`}
          fetchAll={fetchAllSignals}
        />
      </Space>
    </DetailModal>
  );
}

export default ScanDetailModal;
